import type { TFunction } from "i18next";
import type { Review, ShapeViolation } from "../../../api/content/reviewDecoders";
import { findingFocus, findingFocusName, withItemNames } from "../checks/checkFindings";
import { checkKindText, MODEL_CHECK_IDS } from "../checks/checkKinds";
import { conflictTermNames } from "../conflicts/conflictTermNames";
import type { DecisionAction } from "./decisionRules";

/** Violations a send-back report lists at most; the rest it counts. */
const LISTED_IN_REPORT = 20;

/** Resolve checks of either graph through the same titles as the checks panel; a check without a title, such as
 * a consistency check, by its kind, never by its ID. */
export function checkNames(review: Review, ids: readonly string[], t: TFunction): readonly string[] {
  const results = [...(review.checks?.results ?? []), ...(review.checks?.processResults ?? [])];
  return ids.map((id) => {
    const result = results.find((check) => check.checkId === id);
    const kind = result?.kind ?? (MODEL_CHECK_IDS.has(id) ? id : null);
    return result?.title ?? (kind === null ? id : checkKindText(kind, t));
  });
}

/** One report for send-back, passed through the existing decision to the producing step (R-3, F95). */
export function decisionComment(action: DecisionAction, review: Review, prefill: string, t: TFunction, language: string, itemNames: Readonly<Record<string, string>> = {}): string {
  if (action !== "request_changes") return "";
  if (!review.mergeBlocks.blocked) return prefill;
  const blocks = review.mergeBlocks;
  const shapes = blocks.shapeViolations;
  const labels = { ...withItemNames(conflictTermNames(review.conflictCheck, language), review.items, [...shapes.introduced, ...shapes.preExisting]), ...itemNames };
  const checks = checkNames(review, blocks.inconsistencyCheckIds, t);
  const uncompared = checkNames(review, shapes.uncomparedCheckIds, t);
  const line = (violation: ShapeViolation) =>
    `- ${violation.checkTitle ?? violation.checkId} — ${findingFocusName(findingFocus(violation.finding.focus, labels), t)}: ${violation.finding.message}`;
  const report = (listed: number) => [
    t("reviews.mergeBlocks.explanation"),
    checks.length > 0 ? `${t("reviews.mergeBlocks.inconsistent")}\n${checks.map((name) => `- ${name}`).join("\n")}` : "",
    listed > 0 || shapes.unlistedIntroduced > 0 ? [t("reviews.mergeBlocks.introduced"), ...shapes.introduced.slice(0, listed).map(line)].join("\n") : "",
    shapes.introduced.length + shapes.unlistedIntroduced > listed ? t("reviews.mergeBlocks.more", { count: shapes.introduced.length + shapes.unlistedIntroduced - listed }) : "",
    uncompared.length > 0 ? t("reviews.mergeBlocks.uncompared", { checks: uncompared.join(", ") }) : "",
    shapes.preExisting.length > 0 ? t("reviews.mergeBlocks.preExistingCount", { count: shapes.preExisting.length }) : "",
    prefill,
  ].filter((part) => part.trim() !== "").join("\n\n");
  // As many violations as the server's comment limit holds, the rest counted; the text is cut only if even none fit.
  let listed = Math.min(LISTED_IN_REPORT, shapes.introduced.length);
  while (listed > 0 && report(listed).length > review.maximumCommentCharacters) listed -= 1;
  return report(listed).slice(0, review.maximumCommentCharacters);
}
